import React, { useRef } from 'react';
import { Box, Container, Flex, Heading, Text, VStack, HStack, Icon, Divider, Badge, Grid, GridItem, useDisclosure, ScaleFade, SlideFade } from '@chakra-ui/react';
import { Code, Shield, Clock, Lightbulb, MapPin, Terminal, Server, Database, Lock } from 'lucide-react';
import { motion, useScroll, useTransform } from 'framer-motion';
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
export function About() {
  const {
    isOpen,
    onToggle
  } = useDisclosure({
    defaultIsOpen: true
  });
  const containerRef = useRef(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start']
  });
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [100, 0, 0, -100]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.8, 1, 1, 0.8]);
  const infoItems = [{
    icon: Code,
    title: 'Backend Development',
    description: '5+ years experience in backend development and API security',
    color: 'neonBlue.400'
  }, {
    icon: Shield,
    title: 'Security Specialist',
    description: 'Expertise in OWASP standards, penetration testing, and vulnerability assessment',
    color: 'neonPurple.400'
  }, {
    icon: Clock,
    title: 'Founder & CEO',
    description: 'GraySpace Digital, delivering secure platforms for businesses across Africa',
    color: 'neonGreen.400'
  }, {
    icon: Lightbulb,
    title: 'STEM & Robotics',
    description: 'Leads training in underserved schools, serves as Head Judge in robotics competitions',
    color: 'neonBlue.400'
  }, {
    icon: MapPin,
    title: 'Mission',
    description: 'Using technology to enhance security and make education more accessible across Africa',
    color: 'neonPurple.400'
  }];
  return <Box id="about" py={{
    base: 16,
    md: 24
  }} width="100%" bg="darkBg.800" position="relative" borderTop="1px solid rgba(0, 153, 255, 0.1)" borderBottom="1px solid rgba(0, 153, 255, 0.1)" overflow="hidden" ref={containerRef} data-id="element-17">
      {/* Animated background elements */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" opacity="0.03" zIndex="0" bgImage="url('https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80')" bgSize="cover" bgPosition="center" data-id="element-18" />
      {/* Floating tech icons */}
      <MotionBox position="absolute" top="10%" left="5%" color="neonBlue.500" opacity="0.1" zIndex="0" animate={{
      y: [0, -15, 0],
      rotate: [0, 5, 0]
    }} transition={{
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-19">
        <Terminal size={80} data-id="element-20" />
      </MotionBox>
      <MotionBox position="absolute" bottom="15%" right="8%" color="neonPurple.500" opacity="0.1" zIndex="0" animate={{
      y: [0, 20, 0],
      rotate: [0, -5, 0]
    }} transition={{
      duration: 7,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 1
    }} data-id="element-21">
        <Server size={100} data-id="element-22" />
      </MotionBox>
      <MotionBox position="absolute" top="40%" right="15%" color="neonGreen.500" opacity="0.1" zIndex="0" animate={{
      y: [0, -10, 0],
      rotate: [0, 10, 0]
    }} transition={{
      duration: 5,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.5
    }} data-id="element-23">
        <Database size={60} data-id="element-24" />
      </MotionBox>
      <MotionBox position="absolute" bottom="25%" left="12%" color="neonBlue.500" opacity="0.1" zIndex="0" animate={{
      y: [0, 15, 0],
      rotate: [0, -8, 0]
    }} transition={{
      duration: 8,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 2
    }} data-id="element-25">
        <Lock size={70} data-id="element-26" />
      </MotionBox>
      <Container maxW="1200px" position="relative" zIndex="1" data-id="element-27">
        <MotionBox style={{
        opacity,
        y,
        scale
      }} data-id="element-28">
          <VStack spacing={{
          base: 10,
          md: 16
        }} data-id="element-29">
            <VStack spacing={4} textAlign="center" maxW="800px" data-id="element-30">
              <MotionBox initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} data-id="element-31">
                <Heading as="h2" fontSize={{
                base: '3xl',
                md: '4xl',
                lg: '5xl'
              }} fontFamily="mono" position="relative" display="inline-block" bgGradient="linear(to-r, neonBlue.400, neonPurple.400)" bgClip="text" letterSpacing="tight" data-id="element-32">
                  <Box as="span" color="neonBlue.400" mr={2} data-id="element-33">
                    {'<'}
                  </Box>
                  About
                  <Box as="span" color="neonBlue.400" ml={2} data-id="element-34">
                    {' />'}
                  </Box>
                </Heading>
              </MotionBox>
              <Divider width="120px" borderColor="neonBlue.500" borderWidth={2} opacity={0.6} data-id="element-35" />
              <MotionText fontSize={{
              base: 'lg',
              md: 'xl'
            }} color="whiteAlpha.900" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} maxW="700px" data-id="element-36">
                Backend developer and security specialist with a passion for
                building secure, scalable systems that solve real problems.
              </MotionText>
            </VStack>
            {/* Modern card-based layout */}
            <Grid templateColumns={{
            base: '1fr',
            lg: '1fr 1fr'
          }} gap={{
            base: 8,
            md: 12
          }} width="100%" data-id="element-37">
              <GridItem colSpan={1} data-id="element-38">
                <MotionBox bg="darkBg.700" p={{
                base: 6,
                md: 8
              }} borderRadius="xl" boxShadow="0 0 30px rgba(0, 153, 255, 0.15)" border="1px solid rgba(0, 153, 255, 0.2)" initial={{
                opacity: 0,
                x: -50
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5
              }} position="relative" overflow="hidden" height="100%" _before={{
                content: '""',
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '100px',
                height: '100px',
                background: 'radial-gradient(circle, rgba(0, 153, 255, 0.2) 0%, transparent 70%)',
                borderRadius: 'full',
                zIndex: '0'
              }} _after={{
                content: '""',
                position: 'absolute',
                bottom: '-30px',
                left: '-30px',
                width: '80px',
                height: '80px',
                background: 'radial-gradient(circle, rgba(119, 0, 255, 0.15) 0%, transparent 70%)',
                borderRadius: 'full',
                zIndex: '0'
              }} data-id="element-39">
                  <VStack align="flex-start" spacing={{
                  base: 5,
                  md: 6
                }} position="relative" zIndex="1" height="100%" data-id="element-40">
                    {infoItems.map((item, index) => <MotionBox key={index} initial={{
                    opacity: 0,
                    x: -20
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.3,
                    delay: index * 0.1
                  }} width="100%" data-id="element-41">
                        <HStack spacing={4} align="flex-start" _hover={{
                      transform: 'translateX(8px)',
                      transition: 'transform 0.3s ease'
                    }} data-id="element-42">
                          <Flex w={12} h={12} align="center" justify="center" borderRadius="xl" bg={`${item.color.replace('400', '500')}15`} color={item.color} border="1px solid" borderColor={`${item.color.replace('400', '500')}30`} boxShadow={`0 0 15px ${item.color.replace('400', '500')}20`} flexShrink={0} data-id="element-43">
                            <Icon as={item.icon} boxSize={5} data-id="element-44" />
                          </Flex>
                          <VStack align="flex-start" spacing={1} data-id="element-45">
                            <Text fontSize={{
                          base: 'md',
                          md: 'lg'
                        }} fontWeight="bold" color={item.color} fontFamily="mono" data-id="element-46">
                              {item.title}
                            </Text>
                            <Text fontSize={{
                          base: 'sm',
                          md: 'md'
                        }} color="whiteAlpha.800" data-id="element-47">
                              {item.description}
                            </Text>
                          </VStack>
                        </HStack>
                      </MotionBox>)}
                  </VStack>
                </MotionBox>
              </GridItem>
              <GridItem colSpan={1} data-id="element-48">
                <MotionFlex direction="column" height="100%" initial={{
                opacity: 0,
                x: 50
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.5
              }} gap={6} data-id="element-49">
                  {/* Main image with modern styling */}
                  <Box position="relative" borderRadius="xl" overflow="hidden" boxShadow="0 4px 30px rgba(0, 153, 255, 0.3)" height={{
                  base: '300px',
                  md: '350px',
                  lg: '400px'
                }} width="100%" border="1px solid rgba(0, 153, 255, 0.3)" _before={{
                  content: '""',
                  position: 'absolute',
                  top: '0',
                  left: '0',
                  width: '100%',
                  height: '100%',
                  background: 'linear-gradient(135deg, rgba(0, 153, 255, 0.2) 0%, rgba(119, 0, 255, 0.1) 100%)',
                  zIndex: '1',
                  mixBlendMode: 'overlay'
                }} data-id="element-50">
                    <Box as="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/4vAERRzbHDGEDrXjxP2V42/teaching.jpg" alt="George teaching robotics" position="absolute" top="0" left="0" width="100%" height="100%" objectFit="cover" filter="saturate(0.9) contrast(1.1)" transition="transform 0.5s ease" _hover={{
                    transform: 'scale(1.05)'
                  }} data-id="element-51" />
                    {/* Modern overlay with gradient */}
                    <Box position="absolute" top="0" left="0" right="0" bottom="0" bg="linear-gradient(180deg, rgba(10,10,15,0) 0%, rgba(10,10,15,0.8) 70%, rgba(10,10,15,0.9) 100%)" zIndex="2" data-id="element-52" />
                    <VStack position="absolute" bottom="0" left="0" right="0" p={6} align="flex-start" spacing={2} zIndex="3" data-id="element-53">
                      <Badge bg="neonBlue.500" color="white" fontFamily="mono" px={3} py={1} borderRadius="md" boxShadow="0 0 10px rgba(0, 153, 255, 0.5)" data-id="element-54">
                        STEM Education
                      </Badge>
                      <Heading size="md" fontWeight="bold" letterSpacing="tight" data-id="element-55">
                        Empowering the next generation through technology
                      </Heading>
                      <Text fontSize="sm" color="whiteAlpha.800" fontStyle="italic" data-id="element-56">
                        Head Judge, Robotics Competitions Ghana
                      </Text>
                    </VStack>
                  </Box>
                  {/* Additional information cards */}
                  <Grid templateColumns={{
                  base: '1fr',
                  sm: '1fr 1fr'
                }} gap={6} height={{
                  lg: 'calc(100% - 400px - 1.5rem)'
                }} data-id="element-57">
                    <MotionBox bg="darkBg.700" borderRadius="xl" p={5} border="1px solid rgba(0, 153, 255, 0.2)" boxShadow="0 0 20px rgba(0, 153, 255, 0.1)" initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: 0.2
                  }} position="relative" overflow="hidden" _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: '-20px',
                    right: '-20px',
                    width: '100px',
                    height: '100px',
                    background: 'radial-gradient(circle, rgba(0, 255, 133, 0.1) 0%, transparent 70%)',
                    borderRadius: 'full',
                    zIndex: '0'
                  }} height="100%" data-id="element-58">
                      <VStack align="flex-start" spacing={3} position="relative" zIndex="1" data-id="element-59">
                        <Flex w={10} h={10} align="center" justify="center" borderRadius="lg" bg="rgba(0, 255, 133, 0.1)" color="neonGreen.400" border="1px solid" borderColor="rgba(0, 255, 133, 0.3)" data-id="element-60">
                          <Icon as={Database} boxSize={5} data-id="element-61" />
                        </Flex>
                        <Heading size="sm" fontFamily="mono" color="neonGreen.400" data-id="element-62">
                          Tech Stack
                        </Heading>
                        <VStack align="flex-start" spacing={1} data-id="element-63">
                          <Text fontSize="sm" data-id="element-64">• Django/DRF</Text>
                          <Text fontSize="sm" data-id="element-65">• Python/Node.js</Text>
                          <Text fontSize="sm" data-id="element-66">• PostgreSQL/MongoDB</Text>
                          <Text fontSize="sm" data-id="element-67">• AWS/Docker</Text>
                        </VStack>
                      </VStack>
                    </MotionBox>
                    <MotionBox bg="darkBg.700" borderRadius="xl" p={5} border="1px solid rgba(119, 0, 255, 0.2)" boxShadow="0 0 20px rgba(119, 0, 255, 0.1)" initial={{
                    opacity: 0,
                    y: 20
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.4,
                    delay: 0.3
                  }} position="relative" overflow="hidden" _after={{
                    content: '""',
                    position: 'absolute',
                    bottom: '-20px',
                    right: '-20px',
                    width: '100px',
                    height: '100px',
                    background: 'radial-gradient(circle, rgba(119, 0, 255, 0.1) 0%, transparent 70%)',
                    borderRadius: 'full',
                    zIndex: '0'
                  }} height="100%" data-id="element-68">
                      <VStack align="flex-start" spacing={3} position="relative" zIndex="1" data-id="element-69">
                        <Flex w={10} h={10} align="center" justify="center" borderRadius="lg" bg="rgba(119, 0, 255, 0.1)" color="neonPurple.400" border="1px solid" borderColor="rgba(119, 0, 255, 0.3)" data-id="element-70">
                          <Icon as={Shield} boxSize={5} data-id="element-71" />
                        </Flex>
                        <Heading size="sm" fontFamily="mono" color="neonPurple.400" data-id="element-72">
                          Security Focus
                        </Heading>
                        <VStack align="flex-start" spacing={1} data-id="element-73">
                          <Text fontSize="sm" data-id="element-74">• API Security</Text>
                          <Text fontSize="sm" data-id="element-75">• Penetration Testing</Text>
                          <Text fontSize="sm" data-id="element-76">• Auth Systems</Text>
                          <Text fontSize="sm" data-id="element-77">• OWASP Standards</Text>
                        </VStack>
                      </VStack>
                    </MotionBox>
                  </Grid>
                </MotionFlex>
              </GridItem>
            </Grid>
          </VStack>
        </MotionBox>
      </Container>
    </Box>;
}